import Header from "../../components/ui/header";
import CollapsibleCard from "../../components/ui/accordion";
import { formatCurrency } from "../../components/ui/money";

type HistoryRecordType = Record<string, Record<string, any>>;

const i18n: Record<string, string> = {
  electricity: "Електроенергія",
  losses: "Нарахування",
  services: "Послуги",
  service_type_of_service_name: "Тип послуги",
  day: "День",
  night: "Ніч",
  year: "Рік",
  once: "Одноразово",
  total: "Разом",
};

const allowedKeys: Record<string, string> = {
  // registrar_number: "№ Документа",
  dateTime: "Дата",
  serviceName: "Назва послуги",
  forService: "За послугу",
  dayNight: "День/Ніч",
  amount: "Сума",
};

const allowedHeadersAmount: Record<string, string> = {
  Expense: 'Сплачено',
  // FinalBalance: 'Залишок',
  // InitialBalance: 'Нараховано',
};

const allowedKeysAmount: Record<string, string> = {
  expense: 'Сплачено',
  final_balance: 'Залишок',
  initial_balance: 'Нараховано',
};

type RenderHistoryRecordProps = {
  history: Record<string, any>;
  account: string;
  sortedKeys: string[];
  includeKeys?: string[];
  excludeKeys?: string[];
  includeNames?: string[];
  excludeNames?: string[];
};

const RenderHistoryRecord: React.FC<RenderHistoryRecordProps> = (
  {
    history,
    account,
    sortedKeys,
    includeKeys = [],
    excludeKeys = [],
    includeNames = [],
    excludeNames = [],
  }) => {

  const tI18n = (key: string) => {
    const translation = i18n[key];
    return translation ? translation : key;
  }

  const tAllowedKeys = (key: string) => {
    const translation = allowedKeys[key];
    return translation ? translation : key;
  }

  const tAllowedKeysAmount = (key: string) => {
    const translation = allowedKeysAmount[key];
    return translation ? translation : key;
  }

  const renderHistoryItem = (key: string, entry: HistoryRecordType) => {
    // console.log("RenderHistoryRecord renderHistoryItem", {key, entry, includeKeys, excludeKeys} );

    const find = excludeNames.find((name) => {
      if (name && name[0] === "!") {
        name = name.substring(1);
        return !(entry?.["serviceName"] && String(entry?.["serviceName"] ?? "").toLowerCase().includes(name.toLowerCase()))
      } else {
        return entry?.["serviceName"] && String(entry?.["serviceName"] ?? "").toLowerCase().includes(name.toLowerCase())
      }
    });

    if (find) {
      return false; // Do not render the item
    }

    // Check if includeKeys is empty and excludeKeys does not contain the key
    if (includeKeys.length === 0 && !excludeKeys.includes(key)) {
      return true; // Render the item
    }
    // If it's in includeKeys and not in excludeKeys, render the item
    if (includeKeys.includes(key) && !excludeKeys.includes(key)) {
      return true; // Render the item
    }
    // If it's in excludeKeys, do not render the item
    if (excludeKeys.includes(key)) {
      return false; // Do not render the item
    }
    return false; // Default case if none of the conditions are met
  };

  return (
    <div
      key={`title-${account}-${Math.random()}`}
      className="bg-white mb-4"
    >
      <div>
        <div className="text-sm overflow-x-auto">
          <table className="table-auto w-full">
            <thead>
            <tr>
              {Object.entries(allowedKeys)?.map(([key, translate]: [string, any]) => {
                if (key === "amount") {
                  return Object.entries(allowedHeadersAmount)?.map(([keyA, translateA]: [string, any]) => {
                    return (
                      <th className="border border-gray-200 text-red-700"
                          key={`${history?.[0]?.["dateTime"]}-${key}-${keyA}`}>
                        {translateA}
                      </th>
                    )
                  })
                }
                return (
                  <th className="border border-gray-200 text-red-700"
                      key={`${history?.[0]?.["dateTime"]}-${key}`}>{translate}</th>
                )
              })}
            </tr>
            </thead>
            <tbody className={"text-sm text-gray-900"}>
            {Object.values(sortedKeys)?.map((dateTime: string) => {
              return (
                <>
                  {Object.entries(history?.[dateTime] || {})?.map(([noRecord, entry]: [string, any]) => {
                    return (
                      <tr key={`tr-${dateTime}-${noRecord}`}>
                        {
                          Object.entries(allowedKeys)?.map(([key, translate]: [string, any]) => {
                            if (!renderHistoryItem(entry["service"], entry)) return null;
                            if (key === "amount") {
                              return Object.entries(allowedHeadersAmount)?.map(([keyA, translateA]: [string, any]) => {
                                return (
                                  <td className="border border-gray-200 text-left p-2"
                                      key={`${dateTime}-${noRecord}-${key}-${keyA}`}>
                                    {formatCurrency(entry[key][keyA] ? entry[key][keyA] : "0.0")}
                                  </td>
                                )
                              })
                            }
                            return (entry[key] ? (
                                <td className="border border-gray-200 text-left p-2"
                                    key={`${dateTime}-${noRecord}-${key}`}>
                                  {tI18n(entry[key]?.toString())}
                                </td>) : (
                                <td className="border border-gray-200 text-left p-2"
                                    key={`${dateTime}-${noRecord}-${key}`}>
                                  -
                                </td>)
                            )
                          })
                        }
                      </tr>
                    )
                  })}
                </>
              )
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
};

export const HistoryViewer = (
  {
    history,
    sortedKeys,
    account,
    openState = false,
    title = "",
    includeKeys = [],
    excludeKeys = [],
    includeNames = [],
    excludeNames = [],
  }: {
    history: HistoryRecordType
    sortedKeys: string[]
    account: string
    title?: string
    includeKeys?: string[];
    excludeKeys?: string[];
    includeNames?: string[];
    excludeNames?: string[];
    openState?: boolean
    className?: string
  }) => {
  return (
    <Header className={"border rounded-lg"} key={`header-${account || ''}`}>
      <CollapsibleCard title={title || ''} open={openState || false} className="grid grid-cols-1 divide-y" more={false}>
        <RenderHistoryRecord
          key={`RenderRecord-${account}`}
          history={history}
          account={account}
          sortedKeys={sortedKeys}
          includeKeys={includeKeys}
          excludeKeys={excludeKeys}
          includeNames={includeNames || []}
          excludeNames={excludeNames || []}
        />
      </CollapsibleCard>
    </Header>
  );
};
